import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { HardDrive, FolderOpen, AlertCircle } from 'lucide-react';

const DriveSettings = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [driveConnected, setDriveConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState('');
  const [folders, setFolders] = useState([]);
  const [rootFolderName, setRootFolderName] = useState('LogisticsHub Files');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user_profile') || '{}');
    } catch (error) {
      return {};
    }
  }, []);

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Please log in to manage Drive settings.');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const getErrorMessage = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      return data?.detail || fallbackMessage;
    } catch (error) {
      return fallbackMessage;
    }
  };

  const fetchDriveInfo = async () => {
    if (!backendUrl) {
      setErrorMessage('Backend URL is not configured.');
      return;
    }
    try {
      setLoading(true);
      setErrorMessage('');
      const headers = getAuthHeaders();

      const [statusResponse, foldersResponse] = await Promise.all([
        fetch(`${backendUrl}/api/drive/status`, { headers }),
        fetch(`${backendUrl}/api/drive/folders`, { headers })
      ]);

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setDriveConnected(Boolean(statusData.connected));
        setConnectedEmail(statusData.connected_email || '');
      }

      if (foldersResponse.ok) {
        const foldersData = await foldersResponse.json();
        setFolders(foldersData.folders || []);
        if (foldersData.root_folder) {
          setRootFolderName(foldersData.root_folder);
        }
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load Drive settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriveInfo();

    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === 'true') {
      params.delete('drive_connected');
      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const handleDriveConnect = async () => {
    try {
      setErrorMessage('');
      const headers = getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/drive/connect`, { headers });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to connect Google Drive.');
        throw new Error(message);
      }
      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to connect Google Drive.');
    }
  };

  const handleDriveDisconnect = async () => {
    try {
      setErrorMessage('');
      const headers = getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/drive/disconnect`, {
        method: 'POST',
        headers
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to disconnect Google Drive.');
        throw new Error(message);
      }
      setDriveConnected(false);
      setConnectedEmail('');
      setFolders([]);
      setSuccessMessage('Google Drive disconnected.');
    } catch (error) {
      setErrorMessage(error.message || 'Failed to disconnect Google Drive.');
    }
  };

  return (
    <div className="space-y-6" data-testid="drive-settings-page">
      <div className="flex items-center gap-3">
        <HardDrive className="w-6 h-6 text-accent-blue" />
        <div>
          <h2 className="text-2xl font-bold text-text-dark font-manrope">Drive Settings</h2>
          <p className="text-sm text-text-secondary font-ibm">Manage Google Drive connection for all modules.</p>
        </div>
      </div>

      {(errorMessage || successMessage) && (
        <div className="space-y-3">
          {errorMessage && (
            <div
              data-testid="drive-settings-error"
              className="rounded-lg border border-accent-red/40 bg-accent-red/10 px-4 py-3 text-sm text-accent-red font-ibm"
            >
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div
              data-testid="drive-settings-success"
              className="rounded-lg border border-accent-green/40 bg-accent-green/10 px-4 py-3 text-sm text-accent-green font-ibm"
            >
              {successMessage}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div
          data-testid="drive-settings-loading"
          className="rounded-lg border border-accent-blue/30 bg-accent-blue/10 px-4 py-3 text-sm text-accent-blue font-ibm"
        >
          Loading Drive settings...
        </div>
      )}

      <Card className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-text-dark font-manrope">Connection Status</h3>
            <p data-testid="drive-connection-status" className="text-sm text-text-secondary font-ibm mt-1">
              {driveConnected
                ? 'Google Drive is connected for all users.'
                : 'Google Drive is not connected yet.'}
            </p>
            {connectedEmail && (
              <p data-testid="drive-connected-email" className="text-sm text-text-secondary font-ibm mt-1">
                Connected account: {connectedEmail}
              </p>
            )}
          </div>

          {isSuperAdmin ? (
            driveConnected ? (
              <Button
                data-testid="disconnect-drive-button"
                onClick={handleDriveDisconnect}
                className="bg-accent-red hover:bg-accent-red/90 text-white font-ibm font-semibold h-10 px-6 rounded-lg"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                data-testid="connect-drive-button"
                onClick={handleDriveConnect}
                className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-10 px-6 rounded-lg"
              >
                Connect Drive
              </Button>
            )
          ) : (
            <div className="flex items-center gap-2 text-sm text-text-secondary font-ibm">
              <AlertCircle className="w-4 h-4 text-accent-orange" />
              Only Super Admin can manage this connection.
            </div>
          )}
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-accent-blue" />
            <h3 className="text-lg font-semibold text-text-dark font-manrope">Drive Folders</h3>
          </div>
        </div>
        <div className="p-6">
          <p data-testid="drive-root-folder" className="text-sm text-text-secondary font-ibm mb-4">
            Root folder: {rootFolderName}
          </p>
          {folders.length === 0 ? (
            <p data-testid="drive-folders-empty" className="text-sm text-text-secondary font-ibm">
              No module folders created yet. Folders appear after the first upload.
            </p>
          ) : (
            <div className="space-y-3" data-testid="drive-folder-list">
              {folders.map((folder) => (
                <div key={folder.folder_id} className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-text-dark font-ibm">{folder.module_name}</p>
                    <p className="text-xs text-text-secondary font-ibm">Folder ID: {folder.folder_id}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DriveSettings;
